import { Component, Injector, Input, OnInit } from '@angular/core';
import { StatisticsService } from 'bookraw/shared/services/statistics.service';
import { DatePipe } from 'bookraw/shared/pipes/date.pipe';
import * as moment from 'moment';
import { SchoolClass } from 'bookraw/shared/models/school-class.model';
import { SchoolClassesService } from 'bookraw/shared/services/school-classes.service';
import { ReadGoalStat } from 'bookraw/shared/models/statistic.model';

@Component({
  selector: 'app-statistics-calendar',
  templateUrl: './statistics-calendar.component.html',
  styleUrls: ['./statistics-calendar.component.scss'],
  providers: [DatePipe],
})
export class StatisticsCalendarComponent implements OnInit {

  @Input() entityId: number = 0;
  @Input() type: string = 'student';
  @Input() schoolClasses: SchoolClass[] = [];

  goalMinute: number = 0;
  loadingStat: boolean = false;
  monthStatistics: ReadGoalStat[] = [];
  selectedMonth = moment().format('YYYY-MM');
  selectedDate = moment().format('YYYY-MM-DD');
  month: ReadGoalStat[][] = [];
  selectedDateProgress: string;
  selectedDateReadTime: string;
  filterStatSchoolClass: number;
  selectedSchoolClassId: number;

  constructor(
    public service: StatisticsService,
    injector: Injector,
    private schoolClassesService: SchoolClassesService
  ) {
    this.schoolClassesService.resetQueryParams();
  }

  ngOnInit() {
    this.service.goalSettingsChanged.subscribe(() => {
      this.getReadGoalStat();
    });

    this.getReadGoalStat();
  }

  private getReadGoalStat() {
    this.loadingStat = true
    this.service.resetQueryParams('dailyStat');
    this.service.setQueryParam(
      'filters[dateFrom]',
      // subtractolunk 1 napot, mivel az all stat oldalon nincsennek backenden elmentve a streak értékek
      // hanem számolva vannak, ahhoz hogy kell-e összekötés vagy sem így fogjuk meg tudni
      // Note: kerülhetett volna backendre is ennek kezelése, de ez volt a leggyorsabb megoldás
      moment(moment(this.selectedMonth)).subtract('1', 'day').format('YYYY-MM-DD'),
      'dailyStat'
    );
    this.service.setQueryParam(
      'filters[dateTo]',
      moment(moment(this.selectedMonth)).endOf('month').format('YYYY-MM-DD'),
      'dailyStat'
    );

    if (this.selectedSchoolClassId) {
      this.service.setQueryParam('filters[schoolClassId]', this.selectedSchoolClassId.toString(), 'dailyStat');
    } else if (this.type === 'student') {
      this.service.setQueryParam('filters[userId]', this.entityId.toString(), 'dailyStat');
    }

    this.service.getReadGoalStat().subscribe((response) => {
      this.monthStatistics = this.generateMonthList(response.result.list);
      this.goalMinute = response.result.daily / 60;
      this.calculateWeekPlacement();
      this.selectDate(
        this.monthStatistics.find((stat) =>
          moment(stat.day).isSame(this.selectedDate, 'day')
        )
      );
      this.loadingStat = false;
    });
  }

  calculateWeekPlacement() {
    this.month = [];
    const startOfMonth = moment(this.selectedMonth, 'YYYY-MM').startOf('month');
    const endOfMonth = moment(this.selectedMonth, 'YYYY-MM').endOf('month');
    const startOfWeek = startOfMonth.clone().startOf('week').day(1);
    // Ha a hónap első napja nem hétfő, akkor visszamegyünk az előző hónap utolsó hétfőjére
    if (startOfWeek.isAfter(startOfMonth)) {
      startOfWeek.subtract(7, 'days');
    }
    const endOfWeek = endOfMonth.clone().endOf('week').day(7);

    let currentWeek: ReadGoalStat[] = [];
    const currentDate = startOfWeek.clone();

    while (currentDate.isSameOrBefore(endOfWeek)) {
      const matchingDay = this.monthStatistics.find((stat) =>
        moment(stat.day).isSame(currentDate, 'day')
      );

      if (matchingDay) {
        // Ha vannak adatok az aktuális napra vonatkozóan, adja hozzá a héthez
        currentWeek.push(matchingDay);
      } else {
        // Ha az aktuális napra nincs adat, adjon hozzá egy üres bejegyzést a héthez
        currentWeek.push(null);
      }

      if (currentDate.day() === 0) {
        // Ha elérkezik a vasárnap, adja hozzá a hetet a hetek tömbjéhez
        this.month.push(currentWeek);
        currentWeek = [];
      }

      currentDate.add(1, 'day');
    }

    // pusholjuk az utolsó hetet a hetek tömbjére
    if (currentWeek.length) {
      this.month.push(currentWeek);
    }
  }

  selectDate(day?: ReadGoalStat) {
    if (day) {
      this.selectedDate = day.day;
      this.selectedDateReadTime = (day.readTime / 60).toFixed(0)
    } else {
      this.selectedDateReadTime = '0';
    }

    this.selectedDateProgress = this.getReadTimeProgress(day);
  }

  /**
   * Read time progress circle százalék megaállapítása a read time és a goal alapján.
   *
   * @param day Adott napi statisztika vagy null, ha nincs nap, vagy undefined is előfordulhat, ezt is így kezeljük
   */
  getReadTimeProgress(day?: ReadGoalStat): string {
    if (day?.readTime && day?.readTimeGoal) {
      const progress = (day.readTime / day.readTimeGoal) * 100;
      if (progress > 100) {
        return '100';
      } else {
        return progress.toFixed(0);
      }
    } else {
      return '0';
    }
  }

  /**
   * Van-e az adott napon read time rekord egyáltalán.
   *
   * @param day Adott napi statisztika vagy null, ha nincs nap, vagy undefined is előfordulhat, ezt is így kezeljük
   */
  hasReadTime(day?: ReadGoalStat): boolean {
    return day?.readTime !== undefined && day?.readTime !== null;
  }

  changeMonth(change: number) {
    this.selectedMonth = moment(this.selectedMonth, 'YYYY-MM').add(change, 'month').format('YYYY-MM');
    this.selectedDate = moment(this.selectedMonth).startOf('month').format('YYYY-MM-DD');

    this.getReadGoalStat();
  }

  private generateMonthList(list: ReadGoalStat[]) {
    // Végigmegyünk a hónap napjain ha nincs a listában adat, akkor hozzáadunk egy üres bejegyzést a nappal a listához
    const monthList: ReadGoalStat[] = [];
    const startOfMonth = moment(this.selectedMonth, 'YYYY-MM').startOf('month');
    const endOfMonth = moment(this.selectedMonth, 'YYYY-MM').endOf('month');
    const currentDate = startOfMonth.clone();
    // Üres tömb készítés a napokkal
    while (currentDate.isSameOrBefore(endOfMonth)) {
      monthList.push({
        day: currentDate.format('YYYY-MM-DD'),
        streak: null,
        readTimeGoal: null,
        readTime: null,
      });
      currentDate.add(1, 'day');
    }

    // listában keresés, ha van olyan nap a listában mint ami a monthListben akkor a monthListben lévő napot kicseréljük a listában lévő napra
    for (const item of list) {
      const index = monthList.findIndex((stat) => moment(stat.day).isSame(item.day, 'day'));
      if (index !== -1) {
        monthList[index] = item;
      }
    }

    return monthList;
  }

  filterChanges() {
    this.selectedSchoolClassId = this.filterStatSchoolClass;
    this.getReadGoalStat();
  }
}
